import React, { useEffect, useRef } from 'react';

export type ConfirmTone = 'default' | 'danger';

export type ConfirmOptions = {
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' paints the confirm button red and puts the focus on "Hủy". */
  tone?: ConfirmTone;
};

type ConfirmDialogProps = ConfirmOptions & {
  onDone: (confirmed: boolean) => void;
};

/**
 * The dialog behind confirmDialog(): one question, two buttons; Esc and a
 * click outside mean "Hủy", and Tab stays between the two buttons.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title, message, confirmLabel = 'Đồng ý', cancelLabel = 'Hủy', tone = 'default', onDone,
}) => {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    (tone === 'danger' ? cancelRef : confirmRef).current?.focus();
  }, [tone]);

  useEffect(() => {
    // Captured first and stopped there: a dialog underneath (its own Esc closes it) never sees these keys.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        onDone(false);
      } else if (e.key === 'Tab') {
        e.preventDefault();
        e.stopImmediatePropagation();
        (document.activeElement === confirmRef.current ? cancelRef : confirmRef).current?.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onDone]);

  return (
    <div className="confirm-dialog__overlay" role="presentation" onClick={() => onDone(false)}>
      <div
        className="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={title ? 'confirm-dialog-title' : undefined}
        aria-describedby="confirm-dialog-message"
        onClick={(e) => e.stopPropagation()}
      >
        {title && <h2 id="confirm-dialog-title" className="confirm-dialog__title">{title}</h2>}
        <div id="confirm-dialog-message" className="confirm-dialog__message">{message}</div>
        <div className="confirm-dialog__actions">
          <button ref={cancelRef} type="button" className="confirm-dialog__btn" onClick={() => onDone(false)}>
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={`confirm-dialog__btn confirm-dialog__btn--${tone === 'danger' ? 'danger' : 'primary'}`}
            onClick={() => onDone(true)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
